import { supabaseClient } from "../auth/supabase.service";
import { holdButton } from "./ui-library.js";
import { renderTextArrayEditor } from "./textArrayEditor.js";
import * as icons from "./icons.js";
import { resolveImageUrl } from "./utils.pages.js";

const { autorun, observable } = mobx;

/**
 * Create a reusable table editor for JSONB cell data in Supabase
 * 
 * @param {Object} config
 * @param {Object} config.dom - D3 selection for the container
 * @param {Object} config.message - Message utilities {bad, info, progress, good}
 * @param {string} config.table - Supabase table name
 * @param {string} config.recordId - Record ID to update
 * @param {string} config.fieldName - JSONB field name containing the array
 * @param {string} [config.base_url] - Base URL for relative image paths (display only)
 * @param {Array<string>} config.headers - Display column headers
 * @param {Array<Object>} config.columns - Field definitions
 * @param {string} config.columns[].field - Field name in data objects
 * @param {string} config.columns[].type - Field type: 'text', 'image', 'url', 'number', 'boolean', 'kvp', 'options', 'textarray'
 * @param {string} config.columns[].label - Label for editing form
 * @param {boolean} [config.columns[].required] - Whether field is required
 * @param {number} config.columns[].columnIndex - Which display column (0-based) this field appears in
 * @param {Array<string>} [config.columns[].keys] - For 'kvp' type: array of allowed keys
 * @param {Array<string>} [config.columns[].values] - For 'options' or 'textarray' types: array of allowed values
 * @param {boolean} [config.allowReorder=true] - Enable reorder functionality
 * @param {boolean} [config.allowAdd=true] - Enable add new row
 * @param {boolean} [config.allowEdit=true] - Enable edit row
 * @param {boolean} [config.allowDelete=true] - Enable delete row
 * @param {Function} [config.onBeforeSave] - Transform array before save (array) => array
 * @param {Function} [config.onAfterLoad] - Transform array after load (array) => array
 * @param {Function} [config.validate] - Validate item before save (item) => errorMessage|null
 */
export function createJsonbTableEditor(config) {
  const {
    dom,
    heading = '',
    message,
    table,
    recordId,
    fieldName,
    base_url,
    headers,
    columns,
    allowReorder = true,
    allowAdd = true,
    allowEdit = true,
    allowDelete = true,
    onBeforeSave,
    onAfterLoad,
    validate,
    filePicker
  } = config;

  const { bad, info, progress, good } = message;
  let fileNameByUrl = new Map();

  async function loadFileNameLookup() {
    if (!filePicker) {
      fileNameByUrl = new Map();
      return;
    }
    try {
      const { data, error } = await supabaseClient
        .from('files')
        .select('name, url');
      if (error) {
        fileNameByUrl = new Map();
        return;
      }
      fileNameByUrl = new Map((data || [])
        .filter(f => f?.url)
        .map(f => [f.url, f.name || f.url]));
    } catch (_) {
      fileNameByUrl = new Map();
    }
  }

  function getFriendlyFileName(url = '') {
    return fileNameByUrl.get(url) || '';
  }

  const state = observable({ 
    items: [], 
    originalItems: [],
    isLoading: false, 
    error: null,
    mode: 'normal' // 'normal' or 'reorder'
  });

  // Group columns by their columnIndex
  const displayColumns = headers.map((header, index) => {
    const fields = columns.filter(col => col.columnIndex === index);
    return { header, fields };
  });

  // Data Model Layer
  const DataModel = {
    async fetch() {
      const { data, error } = await supabaseClient
        .from(table)
        .select(fieldName)
        .eq('id', recordId)
        .single();
      
      if (error) return { data: null, error };
      
      let items = data?.[fieldName] || [];
      if (onAfterLoad) items = onAfterLoad(items);
      return { data: items, error: null };
    },

    async save(items) {
      let itemsToSave = [...items];
      if (onBeforeSave) itemsToSave = onBeforeSave(itemsToSave);
      
      const { error } = await supabaseClient
        .from(table)
        .update({ [fieldName]: itemsToSave })
        .eq('id', recordId);
      
      return { error };
    }
  };

  // Controller Layer
  const Controller = {
    async load() {
      state.isLoading = true;
      state.error = null;
      state.mode = 'normal';
      
      const { data, error } = await DataModel.fetch();
      
      if (error) {
        state.error = error;
        console.error('Error loading data', error);
        dom.html('');
        dom.append('p').text('Error loading data.');
        return;
      }
      
      state.items = data.map((item, index) => ({ ...item, _index: index }));
      state.originalItems = JSON.parse(JSON.stringify(data));
      await loadFileNameLookup();
      state.isLoading = false;
      renderTable();
    },

    async addItem(item) {
      if (validate) {
        const error = validate(item);
        if (error) {
          message.bad(error);
          return;
        }
      }
      
      const newItems = [...state.originalItems, item];
      const { error } = await DataModel.save(newItems);
      
      if (error) {
        console.error('Error adding item', error);
        message.bad('Error adding item');
        return;
      }
      
      await this.load();
    },

    async updateItem(index, updatedItem) {
      if (validate) {
        const error = validate(updatedItem);
        if (error) {
          message.bad(error);
          return;
        }
      }
      
      const newItems = [...state.originalItems];
      newItems[index] = updatedItem;
      
      const { error } = await DataModel.save(newItems);
      
      if (error) {
        console.error('Error updating item', error);
        message.bad('Error updating item');
        return;
      }
      
      await this.load();
    },

    async deleteItem(index) {
      const newItems = state.originalItems.filter((_, i) => i !== index);
      const { error } = await DataModel.save(newItems);
      
      if (error) {
        console.error('Error deleting item', error);
        message.bad('Error deleting item');
        return;
      }
      
      await this.load();
    },

    async saveReorder() {
      const reorderedItems = state.items.map(item => {
        const { _index, _originalIndex, ...cleanItem } = item;
        return cleanItem;
      });
      
      const { error } = await DataModel.save(reorderedItems);
      
      if (error) {
        console.error('Error saving order', error);
        message.bad('Error saving order');
        return;
      }
      
      message.good('Order saved', 2000);
      await this.load();
    },

    cancelReorder() {
      this.load();
    },

    moveUp(index) {
      if (index === 0) return;
      const newItems = [...state.items];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      state.items = newItems;
      state.mode = 'reorder';
      updateTableRows();
    },

    moveDown(index) {
      if (index === state.items.length - 1) return;
      const newItems = [...state.items];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      state.items = newItems;
      state.mode = 'reorder';
      updateTableRows();
    }
  };

  // Render Layer
  let tableElement, buttonsElement;

  function renderTable() {
    dom.html('');

    const tableWrapper = dom.append('section').attr('class', 'jsonb-table-editor-wrapper');
    const headerElement = tableWrapper.append("div").class("admin-section-header");
    if(heading) headerElement.append("h1").text(heading);
    if(config.comment) tableWrapper.append("p").style("color", "#999").style("margin-bottom", "10px").text(config.comment);

    tableElement = tableWrapper.append('table').attr('class', 'admin-table');
    const headerRow = tableElement.append('tr');
    
    headerRow.append('th').text('order');
    
    displayColumns.forEach(col => headerRow.append('th').text(col.header));
    headerRow.append('th').text('actions');

    updateTableRows();

    // Buttons
    buttonsElement = headerElement.append('div').attr('class', 'actionsbox');
    updateButtons();
  }

  function validatePayload(payload) {
    for (const col of columns) {
      const value = payload[col.field];
      
      // Check required fields
      if (col.required) {
        if (col.type === 'boolean') {
          if (typeof value !== 'boolean') {
            return `${col.label || col.field} is required and must be true or false`;
          }
        } else if (col.type === 'textarray') {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return `${col.label || col.field} is required`;
          }
        } else if (col.type === 'options') {
          if (!value) {
            return `${col.label || col.field} is required`;
          }
        } else {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            return `${col.label || col.field} is required`;
          }
        }
      }
      
      // Validate options type against allowed values
      if (col.type === 'options' && col.values && value) {
        if (!col.values.includes(value)) {
          return `${col.label || col.field} must be one of: ${col.values.join(', ')}`;
        }
      }
      
      // Validate textarray type against allowed values
      if (col.type === 'textarray' && col.values && value && Array.isArray(value)) {
        for (const item of value) {
          if (!col.values.includes(item)) {
            return `${col.label || col.field} contains invalid value: ${item}. Must be one of: ${col.values.join(', ')}`;
          }
        }
      }
    }
    return null;
  }

  function renderCellContent(cell, displayCol, d) {
    // Display column contains one or more fields
    const container = cell.append('div').attr('class', 'grouped-cell');
    const hasMultipleFields = displayCol.fields.length > 1;
    
    displayCol.fields.forEach(fieldDef => {
      const value = d[fieldDef.field];
      if (value !== null && value !== undefined) {
        const fieldDiv = container.append('div')
          .attr('class', `field-${fieldDef.field}`);
        
        // Add label if there are multiple fields in this column
        if (hasMultipleFields && fieldDef.label) {
          fieldDiv.append('span')
            .attr('class', 'field-label')
            .text(fieldDef.label + ': ');
        }
        
        if (fieldDef.type === 'url') {
          let displayText = value;
          if (fieldDef.displayshort !== null && fieldDef.displayshort !== undefined) {
            displayText = value.substring(0, fieldDef.displayshort);
          }
          fieldDiv.append('a')
            .attr('href', value)
            .attr('target', '_blank')
            .style('font-size', '0.9em')
            .style('word-break', 'break-all')
            .text(displayText);
        } else if (fieldDef.type === 'image') {
          const imageHtml = icons[value] || `<img src="${resolveImageUrl(value, base_url)}" alt="${fieldDef.label || fieldDef.field}" onerror="this.alt='no image'" />`;
          fieldDiv.html(imageHtml);
        } else if (fieldDef.type === 'kvp') {
          // Key-value pair object: display as "key value, key value"
          if (typeof value === 'object' && value !== null) {
            const kvpText = Object.entries(value)
              .map(([k, v]) => `${k} ${v}`)
              .join(', ');
            fieldDiv.append('span')
              .attr('class', 'field-value kvp-value')
              .text(kvpText);
          }
        } else if (fieldDef.type === 'textarray') {
          // Comma-separated text array: display with line breaks
          if (Array.isArray(value) && value.length > 0) {
            value.forEach((v, i) => {
              fieldDiv.append('span')
                .attr('class', 'field-value')
                .text(v);
              if (i < value.length - 1) {
                fieldDiv.append('br');
              }
            });
          }
        } else if (fieldDef.type === 'boolean') {
          // Boolean value: display as "true" or "false"
          fieldDiv.attr('style', 'margin-bottom: 4px;');
          fieldDiv.append('span')
            .attr('class', 'field-value')
            .text(String(value));
        } else {
          // text, number, options, or other types
          fieldDiv.attr('style', 'margin-bottom: 4px;');
          fieldDiv.append('span')
            .attr('class', 'field-value')
            .text(value);
        }
      }
    });
  }

  function updateTableRows() {
    // Show empty state if no items
    if (state.items.length === 0) {
      const colSpan = displayColumns.length + 2; // order + actions
      tableElement.selectAll('tr.data-row, tr.empty-row').remove();
      tableElement.append('tr')
        .attr('class', 'empty-row')
        .append('td')
        .attr('colspan', colSpan)
        .style('text-align', 'center')
        .style('padding', '20px')
        .style('color', '#999')
        .style('font-style', 'italic')
        .text('This table is empty');
      return;
    }

    // Remove empty row if it exists
    tableElement.selectAll('tr.empty-row').remove();

    const rows = tableElement.selectAll('tr.data-row')
      .data(state.items, (d, i) => i)
      .join(
        enter => {
          const rows = enter.append('tr').attr('class', 'data-row');
          
          rows.each(function(d, i) {
            const row = d3.select(this);
            
            // Order controls - always render but disable if not allowed
            const orderCell = row.append('td').class('order-buttons');
            const buttonsDiv = orderCell.append('div').class("actionsbox column");
            buttonsDiv.append('div')
              .class('button micro up')
              .classed('disabled', !allowReorder)
              .text('▲')
              .on('click', () => Controller.moveUp(i));
            buttonsDiv.append('div')
              .class('button micro down')
              .classed('disabled', !allowReorder)
              .text('▼')
              .on('click', () => Controller.moveDown(i));
            
            // Data columns
            displayColumns.forEach(displayCol => {
              const cell = row.append('td');
              renderCellContent(cell, displayCol, d);
            });
            
            // Actions - always render but disable if not allowed
            const actionsCell = row.append('td').append('div').attr('class', 'actionsbox column');
            
            actionsCell.append('span')
              .text('Edit')
              .class('button micro')
              .classed('disabled', !allowEdit)
              .on('click', () => renderEditForm(d._index, d, row));
            
            const deleteButton = actionsCell.append('span')
              .text('Delete')
              .class('button delete hold-btn micro')
              .classed('disabled', !allowDelete);
            if (allowDelete) {
              holdButton(deleteButton, () => Controller.deleteItem(d._index));
            }
          });
          
          return rows;
        },
        update => {
          update.each(function(d, i) {
            const row = d3.select(this);
            
            // Update order buttons click handlers and classes
            row.select('.button.up')
              .class('button micro up')
              .classed('disabled', !allowReorder)
              .on('click', () => Controller.moveUp(i));
            row.select('.button.down')
              .class('button micro down')
              .classed('disabled', !allowReorder)
              .on('click', () => Controller.moveDown(i));
            
            // Update data cells
            let cellIndex = allowReorder ? 1 : 0;
            displayColumns.forEach(displayCol => {
              const cell = d3.select(row.node().cells[cellIndex++]);
              cell.html('');
              renderCellContent(cell, displayCol, d);
            });
            
            // Update actions - always render but disable if not allowed
            const actionsCell = d3.select(row.node().cells[row.node().cells.length - 1]);
            actionsCell.html('');
            const actionsDiv = actionsCell.append('div').attr('class', 'actionsbox column');
            
            actionsDiv.append('span')
              .text('Edit')
              .class('button micro')
              .classed('disabled', !allowEdit)
              .on('click', () => renderEditForm(d._index, d, row));
            
            const deleteButton = actionsDiv.append('span')
              .text('Delete')
              .class('button delete hold-btn micro')
              .classed('disabled', !allowDelete);
            if (allowDelete) {
              holdButton(deleteButton, () => Controller.deleteItem(d._index));
            }
          });
          
          return update;
        }
      );
  }

  function updateButtons() {
    autorun(() => {
      buttonsElement.html('');
      
      if (state.mode === 'reorder') {
        const saveOrderBtn = buttonsElement.append('span')
          .text('Save the new order')
          .attr('class', 'button addnew hold-btn');

        holdButton(saveOrderBtn, () => Controller.saveReorder());
        
        buttonsElement.append('span')
          .text('Cancel reorder')
          .attr('class', 'button delete')
          .on('click', () => Controller.cancelReorder());
      } else {
        buttonsElement.append('span')
          .text('✚ New Item')
          .class('button addnew')
          .classed('disabled', !allowAdd)
          .on('click', (event) => {
            d3.select(event.target).remove();
            renderAddForm();
          });
      }
    });
  }

  function renderFormField(row, col, idPrefix, item) {
    row.append('th').text(col.label + (col.required ? ' *' : ''));
    const td = row.append('td');

    if (col.type === 'image') {
      const actualUrl = item?.[col.field] || '';
      const friendlyName = getFriendlyFileName(actualUrl);
      const displayValue = friendlyName || actualUrl;

      const inputRow = td.append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('gap', '6px');

      const input = inputRow.append('input')
        .attr('type', 'text')
        .attr('placeholder', col.header)
        .attr('id', `${idPrefix}-${col.field}`)
        .attr('class', 'input')
        .attr('data-url-value', actualUrl)
        .style('flex', '1')
        .on('input', function() {
          const previewWrapper = td.select('.preview-wrapper');
          const value = (this.value || '').trim();
          d3.select(this).attr('data-url-value', value);
          const imageHtml = icons[value] || `<img src="${resolveImageUrl(value, base_url)}" alt="Preview" onerror="this.alt='no image'" />`;
          previewWrapper.html(imageHtml);
        })
        .on('blur', function() {
          if (!filePicker) return;
          const stored = d3.select(this).attr('data-url-value') || '';
          const friendly = getFriendlyFileName(stored);
          if (friendly) this.value = friendly;
        });
      
      if (item) input.property('value', displayValue);
      
      // Add comment if provided
      if (col.comment) {
        td.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(col.comment);
      }
      
      const value = item?.[col.field];
      const imageHtml = icons[value] || `<img src="${resolveImageUrl(value, base_url) || ''}" alt="Preview" onerror="this.alt='no image'" />`;
      if (filePicker) {
        inputRow.append('div')
          .attr('class', 'button button-option bigger') 
          .text('⋮')
          .on("click", () => {
            filePicker.open(file => {
              const url = file?.url || file;
              const name = file?.name;
              if (!url) return;
              const friendly = name || getFriendlyFileName(url) || url;
              input
                .attr('data-url-value', url)
                .property('value', friendly);
              const previewHtml = icons[url] || `<img src="${resolveImageUrl(url, base_url) || ''}" alt="Preview" onerror="this.alt='no image'" />`;
              td.select('.preview-wrapper').html(previewHtml);
            });
          })
      }

      td.append('div')
        .attr('class', 'preview-wrapper')
        .style('margin-top', '6px')
        .html(imageHtml);
    } else if (col.type === 'kvp' && col.keys) {
      // Key-value pair editor: checkboxes + inputs for each key
      const kvpContainer = td.append('div').attr('class', 'kvp-editor');
      const existingValue = item?.[col.field] || {};
      
      col.keys.forEach((key, index) => {
        const keyRow = kvpContainer.append('div')
          .attr('class', 'kvp-row')
          .style('margin-bottom', '6px')
          .style('display', 'flex')
          .style('align-items', 'center')
          .style('gap', '8px');
        
        const checkbox = keyRow.append('input')
          .attr('type', 'checkbox')
          .attr('id', `${idPrefix}-${col.field}-${key}-enabled`)
          .attr('class', 'kvp-checkbox')
          .property('checked', existingValue.hasOwnProperty(key));
        
        keyRow.append('label')
          .attr('for', `${idPrefix}-${col.field}-${key}-enabled`)
          .style('min-width', '120px')
          .text(key);
        
        const inputWrapper = keyRow.append('div')
          .style('flex', '1')
          .style('display', 'flex')
          .style('flex-direction', 'column');
        
        const input = inputWrapper.append('input')
          .attr('type', 'text')
          .attr('id', `${idPrefix}-${col.field}-${key}-value`)
          .attr('class', 'input kvp-input')
          .property('disabled', !existingValue.hasOwnProperty(key))
          .attr('value', existingValue[key] || '');
        
        // Add comment if provided
        if (col.comment && col.comment[index]) {
          inputWrapper.append('small')
            .style('color', '#999')
            .style('font-size', '0.85em')
            .style('margin-top', '2px')
            .html(col.comment[index]);
        }
        
        // Enable/disable input based on checkbox
        checkbox.on('change', function() {
          input.property('disabled', !this.checked);
          if (!this.checked) input.property('value', '');
        });
      });
      
      // Store hidden input with field ID for form collection
      td.append('input')
        .attr('type', 'hidden')
        .attr('id', `${idPrefix}-${col.field}`)
        .attr('data-kvp', 'true');
    } else if (col.type === 'boolean') {
      td.append('input')
        .attr('type', 'checkbox')
        .attr('id', `${idPrefix}-${col.field}`)
        .attr('class', 'checkbox')
        .property('checked', item?.[col.field] === true);
      
      // Add comment if provided
      if (col.comment) {
        td.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(col.comment);
      }
    } else if (col.type === 'options' && col.values) {
      const select = td.append('select')
        .attr('id', `${idPrefix}-${col.field}`)
        .attr('class', 'input');
      
      // Add empty option
      select.append('option')
        .attr('value', '')
        .text('-- Select --');
      
      // Add options from values
      col.values.forEach(value => {
        select.append('option')
          .attr('value', value)
          .property('selected', item?.[col.field] === value)
          .text(value);
      });
      
      // Add comment if provided
      if (col.comment) {
        td.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(col.comment);
      }
    } else if (col.type === 'textarray' && col.values) {
      const currentValue = item?.[col.field] ? item[col.field] : [];
      renderTextArrayEditor(td, col, `${idPrefix}-${col.field}`, currentValue);
      
      // Add comment if provided
      if (col.comment) {
        td.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(col.comment);
      }
    } else {
      const inputType = col.type === 'number' ? 'number' : 'text';
      const input = td.append('input')
        .attr('type', inputType)
        .attr('placeholder', col.header)
        .attr('id', `${idPrefix}-${col.field}`)
        .attr('class', 'input');
      
      if (item) input.attr('value', item[col.field] || '');
      
      // Add comment if provided
      if (col.comment) {
        td.append('small')
          .style('color', '#999')
          .style('font-size', '0.85em')
          .style('margin-top', '4px')
          .style('display', 'block')
          .html(col.comment);
      }
    }
  }

  function renderAddForm() {
    const form = tableElement.append('tr').attr('class', 'data-row add-form');
    const colSpan = displayColumns.length + (allowReorder ? 2 : 1);
    const panel = form.append('td')
      .attr('colspan', colSpan)
      .attr('class', 'panel-wrapper')
      .append('table');

    columns.forEach(col => {
      const row = panel.append('tr');
      renderFormField(row, col, 'new', null);
    });

    const actionsCell = panel.append('tr').append('td')
      .attr('class', 'actions-cell')
      .attr('colspan', 2);

    actionsCell.append('span')
      .attr('class', 'button')
      .text('Cancel')
      .on('click', () => Controller.load());

    actionsCell.append('span')
      .attr('class', 'addnew button')
      .text('Save')
      .on('click', async () => {
        const payload = {};
        
        columns.forEach(col => {
          if (col.type === 'kvp' && col.keys) {
            // Collect key-value pairs from checkboxes and inputs
            const kvpObj = {};
            col.keys.forEach(key => {
              const checkbox = form.select(`#new-${col.field}-${key}-enabled`).node();
              const input = form.select(`#new-${col.field}-${key}-value`).node();
              if (checkbox?.checked && input?.value) {
                kvpObj[key] = input.value;
              }
            });
            payload[col.field] = Object.keys(kvpObj).length > 0 ? kvpObj : null;
          } else if (col.type === 'boolean') {
            const checkbox = form.select(`#new-${col.field}`).node();
            payload[col.field] = checkbox?.checked || false;
          } else if (col.type === 'textarray' && col.values) {
            // Get the selection order array from the hidden input
            const hiddenInput = form.select(`#new-${col.field}`).node();
            const selectionOrder = hiddenInput?._selectionOrder || [];
            payload[col.field] = selectionOrder.length > 0 ? selectionOrder : null;
          } else {
            const inputNode = form.select(`#new-${col.field}`).node();
            const value = col.type === 'image'
              ? (inputNode?.getAttribute('data-url-value') || inputNode?.value)
              : inputNode?.value;
            if (value !== undefined) {
              payload[col.field] = value || null;
            }
          }
        });

        const validationError = validatePayload(payload);
        if (validationError) {
          message.bad(validationError);
          return;
        }

        await Controller.addItem(payload);
      });
  }

  function renderEditForm(index, item, row) {
    row.html('').classed('edit-form', true);
    const colSpan = displayColumns.length + (allowReorder ? 2 : 1);
    const panel = row.append('td')
      .attr('colspan', colSpan)
      .attr('class', 'panel-wrapper')
      .append('table');

    columns.forEach(col => {
      const rowEl = panel.append('tr');
      renderFormField(rowEl, col, 'edit', item);
    });

    const actionsCell = panel.append('tr').append('td')
      .attr('class', 'actions-cell')
      .attr('colspan', 2);
    actionsCell.append('span')
      .attr('class', 'button bigger')
      .text('Cancel')
      .on('click', () => Controller.load());
    actionsCell.append('span')
      .attr('class', 'addnew button bigger')
      .text('Save')
      .on('click', async () => {
        const payload = {};
        columns.forEach(col => {
          if (col.type === 'kvp' && col.keys) {
            // Collect key-value pairs from checkboxes and inputs
            const kvpObj = {};
            col.keys.forEach(key => {
              const checkbox = row.select(`#edit-${col.field}-${key}-enabled`).node();
              const input = row.select(`#edit-${col.field}-${key}-value`).node();
              if (checkbox?.checked && input?.value) {
                kvpObj[key] = input.value;
              }
            });
            payload[col.field] = Object.keys(kvpObj).length > 0 ? kvpObj : null;
          } else if (col.type === 'boolean') {
            const checkbox = row.select(`#edit-${col.field}`).node();
            payload[col.field] = checkbox?.checked || false;
          } else if (col.type === 'textarray' && col.values) {
            // Get the selection order array from the hidden input
            const hiddenInput = row.select(`#edit-${col.field}`).node();
            const selectionOrder = hiddenInput?._selectionOrder || [];
            payload[col.field] = selectionOrder.length > 0 ? selectionOrder : null;
          } else {
            const inputNode = row.select(`#edit-${col.field}`).node();
            const value = col.type === 'image'
              ? (inputNode?.getAttribute('data-url-value') || inputNode?.value)
              : inputNode?.value;
            if (value !== undefined) {
              payload[col.field] = value || null;
            }
          }
        });

        const validationError = validatePayload(payload);
        if (validationError) {
          message.bad(validationError);
          return;
        }

        await Controller.updateItem(index, payload);
      });


  }

  // Public API
  return {
    load: () => Controller.load(),
    refresh: () => Controller.load(),
    getItems: () => state.items.map(({ _index, _originalIndex, ...item }) => item),
    setItems: (items) => DataModel.save(items).then(() => Controller.load())
  };
}